import { useState } from "react";
import { toast } from "sonner";
import { Reveal, Eyebrow, SectionTitle, WhatsAppIcon } from "./shared";
import { useSite } from "../../context/SiteContext";
import { api, waLink, formatApiError } from "../../lib/api";

const TYPES = ["Religiosa", "Religiosa com efeito civil", "Inter-religiosa", "Bodas", "Renovação de votos", "Ainda não decidimos"];

const EMPTY = {
  name: "", partner_name: "", whatsapp: "", email: "", wedding_date: "",
  wedding_time: "", city: "", venue: "", guests: "", ceremony_type: "", message: "", website: "",
};

const inputCls =
  "w-full bg-white border border-beige focus:border-gold focus:ring-2 focus:ring-gold/20 rounded-xl px-4 py-3 text-sm text-ink placeholder:text-stone2 outline-none transition-all";

export default function AvailabilityForm() {
  const { content } = useSite();
  const [form, setForm] = useState(EMPTY);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  if (!content) return null;
  const c = content.contacts;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const buildWaMessage = () => {
    const lines = [
      "Olá, Padre Lucas. Gostaria de consultar disponibilidade para meu casamento.",
      `Nome: ${form.name}${form.partner_name ? ` & ${form.partner_name}` : ""}`,
      form.wedding_date && `Data: ${form.wedding_date}${form.wedding_time ? ` às ${form.wedding_time}` : ""}`,
      form.city && `Cidade: ${form.city}`,
      form.venue && `Local: ${form.venue}`,
      form.guests && `Convidados: ${form.guests}`,
      form.ceremony_type && `Tipo de cerimônia: ${form.ceremony_type}`,
      form.message && `Mensagem: ${form.message}`,
    ].filter(Boolean);
    return lines.join("\n");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.whatsapp.trim()) {
      toast.error("Preencha ao menos seu nome e WhatsApp.");
      return;
    }
    setSending(true);
    try {
      await api.post("/leads", form);
      setSent(true);
      toast.success("Consulta enviada com sucesso!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="consulte" className="py-20 lg:py-28 bg-olivedark grain relative" data-testid="availability-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="text-center">
            <Eyebrow light>Consulte sua data</Eyebrow>
            <SectionTitle light>Vamos conversar sobre o seu casamento?</SectionTitle>
            <p className="mt-5 text-base sm:text-lg font-light text-cream/80 leading-relaxed max-w-2xl mx-auto">
              Conte um pouco sobre o que vocês estão planejando. A consulta de disponibilidade não gera compromisso.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          {sent ? (
            <div className="mt-12 bg-white/5 border border-gold/30 rounded-3xl p-10 text-center" data-testid="availability-success">
              <p className="font-serif text-3xl text-cream">Obrigado, {form.name.split(" ")[0]}!</p>
              <p className="mt-3 text-cream/75 font-light">
                Sua consulta foi registrada. Para uma resposta ainda mais rápida, continue a conversa agora:
              </p>
              <a
                data-testid="availability-continue-whatsapp"
                href={waLink(c.whatsapp_number, buildWaMessage())}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-7 inline-flex items-center gap-2 bg-whatsapp hover:bg-whatsappdark text-white font-semibold text-sm tracking-wide px-8 py-4 rounded-full transition-all duration-300 hover:scale-[1.03]"
              >
                <WhatsAppIcon className="w-5 h-5" />
                CONTINUAR CONVERSA PELO WHATSAPP
              </a>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-12 bg-warmwhite rounded-3xl p-6 sm:p-10 shadow-2xl shadow-black/30" data-testid="availability-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="field-name" className="block text-xs font-semibold uppercase tracking-[0.15em] text-olive mb-1.5">Nome *</label>
                  <input id="field-name" data-testid="field-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="Seu nome" required />
                </div>
                <div>
                  <label htmlFor="field-partner" className="block text-xs font-semibold uppercase tracking-[0.15em] text-olive mb-1.5">Nome do(a) noivo(a)</label>
                  <input id="field-partner" data-testid="field-partner" className={inputCls} value={form.partner_name} onChange={set("partner_name")} placeholder="Nome do seu amor" />
                </div>
                <div>
                  <label htmlFor="field-whatsapp" className="block text-xs font-semibold uppercase tracking-[0.15em] text-olive mb-1.5">WhatsApp *</label>
                  <input id="field-whatsapp" data-testid="field-whatsapp" className={inputCls} value={form.whatsapp} onChange={set("whatsapp")} placeholder="(31) 99999-9999" required />
                </div>
                <div>
                  <label htmlFor="field-email" className="block text-xs font-semibold uppercase tracking-[0.15em] text-olive mb-1.5">E-mail</label>
                  <input id="field-email" data-testid="field-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="voce@email.com" />
                </div>
                <div>
                  <label htmlFor="field-date" className="block text-xs font-semibold uppercase tracking-[0.15em] text-olive mb-1.5">Data do casamento</label>
                  <input id="field-date" data-testid="field-date" type="date" className={inputCls} value={form.wedding_date} onChange={set("wedding_date")} />
                </div>
                <div>
                  <label htmlFor="field-time" className="block text-xs font-semibold uppercase tracking-[0.15em] text-olive mb-1.5">Horário</label>
                  <input id="field-time" data-testid="field-time" type="time" className={inputCls} value={form.wedding_time} onChange={set("wedding_time")} />
                </div>
                <div>
                  <label htmlFor="field-city" className="block text-xs font-semibold uppercase tracking-[0.15em] text-olive mb-1.5">Cidade</label>
                  <input id="field-city" data-testid="field-city" className={inputCls} value={form.city} onChange={set("city")} placeholder="Belo Horizonte" />
                </div>
                <div>
                  <label htmlFor="field-venue" className="block text-xs font-semibold uppercase tracking-[0.15em] text-olive mb-1.5">Local da cerimônia</label>
                  <input id="field-venue" data-testid="field-venue" className={inputCls} value={form.venue} onChange={set("venue")} placeholder="Sítio, igreja, salão..." />
                </div>
                <div>
                  <label htmlFor="field-guests" className="block text-xs font-semibold uppercase tracking-[0.15em] text-olive mb-1.5">Nº aproximado de convidados</label>
                  <input id="field-guests" data-testid="field-guests" type="number" min="0" className={inputCls} value={form.guests} onChange={set("guests")} placeholder="100" />
                </div>
                <div>
                  <label htmlFor="field-ceremony-type" className="block text-xs font-semibold uppercase tracking-[0.15em] text-olive mb-1.5">Tipo de cerimônia</label>
                  <select id="field-ceremony-type" data-testid="field-ceremony-type" className={inputCls} value={form.ceremony_type} onChange={set("ceremony_type")}>
                    <option value="">Selecione...</option>
                    {TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="mt-4">
                <label htmlFor="field-message" className="block text-xs font-semibold uppercase tracking-[0.15em] text-olive mb-1.5">Mensagem</label>
                <textarea id="field-message" data-testid="field-message" rows={4} className={inputCls} value={form.message} onChange={set("message")} placeholder="Conte um pouco da história de vocês e dos planos para o grande dia..." />
              </div>
              <input
                type="text"
                name="website"
                value={form.website}
                onChange={set("website")}
                className="hidden"
                tabIndex={-1}
                autoComplete="off"
              />
              <button
                data-testid="availability-submit-button"
                type="submit"
                disabled={sending}
                className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-olive hover:bg-olivedark text-cream font-semibold text-sm tracking-[0.15em] px-8 py-4 rounded-full transition-all duration-300 hover:scale-[1.01] active:scale-[0.99] disabled:opacity-60"
              >
                {sending ? "ENVIANDO..." : "CONSULTAR DISPONIBILIDADE"}
              </button>
              <p className="mt-4 text-xs text-stone2 text-center">
                Seus dados são tratados com confidencialidade, conforme nossa Política de Privacidade (LGPD).
              </p>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}
